import React, { useState, useMemo } from 'react'; // eslint-disable-line import/no-default-export
import type { ConciergeDevInfo } from '../types/devOptions';

interface DevInfoPanelProps {
  /** Dev info returned from the backend (devOptions used + effective ranking weights) */
  devInfo: ConciergeDevInfo;
}

/** Safely extract a numeric value from a plain number, string, or DynamoDB-style { value: ... } wrapper. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toNum = (v: any): number => {
  if (v != null && typeof v === 'object' && 'value' in v) return Number(v.value);
  return Number(v);
};

/** Collapsible panel that displays the devOptions and effective ranking weights
 *  used for a specific concierge product search.
 *  Only renders when `?isDev=true` URL param is present. */
const DevInfoPanel: React.FC<DevInfoPanelProps> = ({ devInfo }) => {
  const isDevMode = useMemo(
    () => new URLSearchParams(window.location.search).get('isDev') === 'true',
    [],
  );

  const [expanded, setExpanded] = useState(false);

  if (!isDevMode) return null;

  const { devOptions, effectiveRankingWeights, brandIds, dtcRetailerIds, retailerIds, normalizedQuery, productDomain, brandHints, isFollowUp, priceConstraint, resolvedAngleStrategy } = devInfo;
  const baseWeights = devOptions.rankingWeights;

  /** Render a weight row with base vs effective comparison */
  const WeightRow = ({
    label,
    base,
    effective,
  }: {
    label: string;
    base: unknown;
    effective: unknown;
  }) => {
    const b = toNum(base);
    const e = toNum(effective);
    const delta = e - b;
    const hasChange = Math.abs(delta) > 0.001;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
        <span style={{ minWidth: '70px', color: '#666' }}>{label}</span>
        <span style={{ minWidth: '38px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#999' }}>
          {b.toFixed(2)}
        </span>
        <span style={{ color: '#999', fontSize: '11px' }}>&rarr;</span>
        <span
          style={{
            minWidth: '38px',
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 600,
            color: hasChange ? '#2563eb' : '#444',
          }}
        >
          {e.toFixed(2)}
        </span>
        {hasChange && (
          <span
            style={{
              fontSize: '11px',
              color: delta > 0 ? '#16a34a' : '#dc2626',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            ({delta > 0 ? '+' : ''}
            {delta.toFixed(2)})
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden',
        marginBottom: '8px',
        background: '#fefce8',
      }}
    >
      <button
        onClick={() => setExpanded((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '6px 12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '12px',
          color: '#92400e',
          fontWeight: 500,
        }}
      >
        <span>Search Dev Info</span>
        <span style={{ fontSize: '11px' }}>{expanded ? '\u25B2' : '\u25BC'}</span>
      </button>
      {expanded && (
        <div
          style={{
            padding: '10px 12px',
            fontSize: '12px',
            color: '#555',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            borderTop: '1px solid #e5e7eb',
            background: '#fffef5',
          }}
        >
          {/* Search Config */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Recall:</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: devOptions.useTextRecall ? '#2563eb' : '#16a34a',
                  backgroundColor: devOptions.useTextRecall ? '#eff6ff' : '#f0fdf4',
                  color: devOptions.useTextRecall ? '#2563eb' : '#16a34a',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {devOptions.useTextRecall ? 'Text' : 'KNN'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Sections:</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: devOptions.useTargetedSectionTitles ? '#2563eb' : '#6b7280',
                  backgroundColor: devOptions.useTargetedSectionTitles ? '#eff6ff' : '#f3f4f6',
                  color: devOptions.useTargetedSectionTitles ? '#2563eb' : '#6b7280',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {devOptions.useTargetedSectionTitles ? 'Targeted' : 'Legacy'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>KNN:</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: devOptions.useTwoPhaseFiltering ? '#2563eb' : '#6b7280',
                  backgroundColor: devOptions.useTwoPhaseFiltering ? '#eff6ff' : '#f3f4f6',
                  color: devOptions.useTwoPhaseFiltering ? '#2563eb' : '#6b7280',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {devOptions.useTwoPhaseFiltering ? '2-Phase' : 'PostFilter'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Outlier Filter:</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: devOptions.useOutlierFiltering ? '#16a34a' : '#dc2626',
                  backgroundColor: devOptions.useOutlierFiltering ? '#f0fdf4' : '#fef2f2',
                  color: devOptions.useOutlierFiltering ? '#16a34a' : '#dc2626',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {devOptions.useOutlierFiltering ? 'On' : 'Off'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Search Angles:</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: devOptions.angleStrategy === 'brand' ? '#2563eb' : devOptions.angleStrategy === 'auto' ? '#9333ea' : '#6b7280',
                  backgroundColor: devOptions.angleStrategy === 'brand' ? '#eff6ff' : devOptions.angleStrategy === 'auto' ? '#faf5ff' : '#f3f4f6',
                  color: devOptions.angleStrategy === 'brand' ? '#2563eb' : devOptions.angleStrategy === 'auto' ? '#9333ea' : '#6b7280',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {devOptions.angleStrategy === 'brand' ? 'Brand' : devOptions.angleStrategy === 'auto' ? 'Auto' : 'Generic'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Rerank:</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: devOptions.useOriginalQueryReranking ? '#16a34a' : '#dc2626',
                  backgroundColor: devOptions.useOriginalQueryReranking ? '#f0fdf4' : '#fef2f2',
                  color: devOptions.useOriginalQueryReranking ? '#16a34a' : '#dc2626',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {devOptions.useOriginalQueryReranking ? 'On' : 'Off'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Ratings:</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: devOptions.ignoreRatings ? '#dc2626' : '#16a34a',
                  backgroundColor: devOptions.ignoreRatings ? '#fef2f2' : '#f0fdf4',
                  color: devOptions.ignoreRatings ? '#dc2626' : '#16a34a',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {devOptions.ignoreRatings ? 'Ignored' : `>=${toNum(devOptions.ratingThreshold).toFixed(1)}`}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Availability:</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: devOptions.availabilityFilter === 'apply' ? '#16a34a' : devOptions.availabilityFilter === 'skipFalse' ? '#d97706' : '#dc2626',
                  backgroundColor: devOptions.availabilityFilter === 'apply' ? '#f0fdf4' : devOptions.availabilityFilter === 'skipFalse' ? '#fffbeb' : '#fef2f2',
                  color: devOptions.availabilityFilter === 'apply' ? '#16a34a' : devOptions.availabilityFilter === 'skipFalse' ? '#d97706' : '#dc2626',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {devOptions.availabilityFilter === 'apply' ? 'Required' : devOptions.availabilityFilter === 'skipFalse' ? 'Skip False' : 'Skip All'}
              </span>
            </div>
          </div>

          {/* Row 2: Models & Numeric params */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>LLM:</span>
              <span style={{ fontWeight: 600, color: '#333', fontSize: '11px' }}>{devOptions.llmModel}</span>
              <span style={{ color: '#999', fontSize: '11px' }}>/</span>
              <span style={{ fontWeight: 600, color: '#666', fontSize: '11px' }}>{devOptions.llmModelLight}</span>
              {devOptions.recatModel ? (
                <>
                  <span style={{ color: '#999', fontSize: '11px' }}>/</span>
                  <span style={{ fontWeight: 600, color: '#b45309', fontSize: '11px' }}>recat:{devOptions.recatModel}</span>
                </>
              ) : null}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Angles:</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{toNum(devOptions.numSearchAngles)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Categories:</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{toNum(devOptions.targetCategories)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Max/brand:</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{toNum(devOptions.maxPerBrand)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Max LLM:</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{toNum(devOptions.maxProductsForLLM)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Page:</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{toNum(devOptions.pageSize)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Per sec:</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{toNum(devOptions.productsPerSection)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Buffer:</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{toNum(devOptions.productsBuffer)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Temp:</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{toNum(devOptions.searchTemperature).toFixed(1)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Semantic:</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{toNum(devOptions.semanticThreshold).toFixed(2)}</span>
            </div>
          </div>

          {/* Ranking Weights: Base → Effective */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              <span style={{ color: '#666', fontWeight: 500 }}>Ranking weights</span>
              <span style={{ fontSize: '11px', color: '#999' }}>(base &rarr; effective)</span>
            </div>
            <WeightRow label="semantic" base={baseWeights.semanticWeight} effective={effectiveRankingWeights.semanticWeight} />
            <WeightRow label="sales" base={baseWeights.salesWeight} effective={effectiveRankingWeights.salesWeight} />
            <WeightRow label="content" base={baseWeights.contentWeight} effective={effectiveRankingWeights.contentWeight} />
            <WeightRow label="review" base={baseWeights.reviewWeight} effective={effectiveRankingWeights.reviewWeight} />
            <WeightRow label="reward" base={baseWeights.rewardWeight} effective={effectiveRankingWeights.rewardWeight} />
            <WeightRow label="price" base={baseWeights.priceWeight} effective={effectiveRankingWeights.priceWeight} />
          </div>

          {/* Search Debug Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>
            <span style={{ color: '#666', fontWeight: 500 }}>Search Debug</span>

            {/* Row: Follow-up, Product Domain, Normalized Query */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              {isFollowUp != null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>Follow-up:</span>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: isFollowUp ? '#2563eb' : '#6b7280',
                      backgroundColor: isFollowUp ? '#eff6ff' : '#f3f4f6',
                      color: isFollowUp ? '#2563eb' : '#6b7280',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                  >
                    {isFollowUp ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {devOptions.angleStrategy === 'auto' && resolvedAngleStrategy && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>LLM chose:</span>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: resolvedAngleStrategy === 'brand' ? '#2563eb' : '#6b7280',
                      backgroundColor: resolvedAngleStrategy === 'brand' ? '#eff6ff' : '#f3f4f6',
                      color: resolvedAngleStrategy === 'brand' ? '#2563eb' : '#6b7280',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                  >
                    {resolvedAngleStrategy === 'brand' ? 'Brand' : 'Generic'}
                  </span>
                </div>
              )}
              {productDomain && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>Domain:</span>
                  <span style={{ fontWeight: 600, color: '#333', fontSize: '11px' }}>{productDomain}</span>
                </div>
              )}
              {normalizedQuery && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>Normalized:</span>
                  <span style={{ fontWeight: 600, color: '#333', fontSize: '11px', fontStyle: 'italic' }}>"{normalizedQuery}"</span>
                </div>
              )}
              {priceConstraint && (priceConstraint.min != null || priceConstraint.max != null) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>Price:</span>
                  <span style={{ fontWeight: 600, color: '#b45309', fontSize: '11px' }}>
                    {priceConstraint.min != null && priceConstraint.max != null
                      ? `$${priceConstraint.min} – $${priceConstraint.max}`
                      : priceConstraint.min != null
                        ? `>= $${priceConstraint.min}`
                        : `<= $${priceConstraint.max}`}
                  </span>
                </div>
              )}
            </div>

            {/* Brand Hints */}
            {brandHints && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>Brand Specific:</span>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: brandHints.isBrandSpecific ? '#2563eb' : '#6b7280',
                      backgroundColor: brandHints.isBrandSpecific ? '#eff6ff' : '#f3f4f6',
                      color: brandHints.isBrandSpecific ? '#2563eb' : '#6b7280',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                  >
                    {brandHints.isBrandSpecific ? 'Yes' : 'No'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>Brand Wide:</span>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      border: '1px solid',
                      borderColor: brandHints.isBrandWide ? '#2563eb' : '#6b7280',
                      backgroundColor: brandHints.isBrandWide ? '#eff6ff' : '#f3f4f6',
                      color: brandHints.isBrandWide ? '#2563eb' : '#6b7280',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                  >
                    {brandHints.isBrandWide ? 'Yes' : 'No'}
                  </span>
                </div>
                {brandHints.brandNames?.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#666', fontWeight: 500 }}>Brands:</span>
                    <span style={{ fontWeight: 600, color: '#333', fontSize: '11px' }}>{brandHints.brandNames.join(', ')}</span>
                  </div>
                )}
              </div>
            )}

            {/* Brand IDs & Retailer IDs */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              {brandIds && brandIds.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>Brand IDs:</span>
                  <span style={{ fontWeight: 600, color: '#7c3aed', fontSize: '11px', fontFamily: 'monospace' }}>[{brandIds.join(', ')}]</span>
                </div>
              )}
              {dtcRetailerIds && dtcRetailerIds.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>DTC Retailer IDs:</span>
                  <span style={{ fontWeight: 600, color: '#0891b2', fontSize: '11px', fontFamily: 'monospace' }}>[{dtcRetailerIds.join(', ')}]</span>
                </div>
              )}
              {retailerIds && retailerIds.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#666', fontWeight: 500 }}>Retailer IDs (used):</span>
                  <span style={{ fontWeight: 600, color: '#0891b2', fontSize: '11px', fontFamily: 'monospace' }}>[{retailerIds.join(', ')}]</span>
                </div>
              )}
              {(!brandIds || brandIds.length === 0) && (!retailerIds || retailerIds.length === 0) && (
                <span style={{ fontSize: '11px', color: '#999', fontStyle: 'italic' }}>No brand/retailer filters applied</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevInfoPanel;
